import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { translate } from 'components/Translation';
import AccordionContent from '../../../common/AccordionContent';
import { ECommerceService } from 'services/eCommerce/eCommerce';
import idConfig from 'utils/ecommerce/services/id.config';
import { env } from 'config/app.config';
import { finalize } from 'rxjs/operators';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import JawazakActivationDialog from './subcomponents/JawazakActivationDialog';
import { Package } from 'utils/api/models/numbers/usage';
import { jawazakPackageDescription, jawazakPackageName } from './helpers';
import { getProductId } from 'views/Numbers/Details/Services/helpers/getProductId';
import RoamingInternationalActivationDialog from '../RoamingInternationalActivationDialog';
import { SingleSubscriberProduct } from 'utils/api/models/subscriberProducts/singleSubscriberProduct';
import apiConfig from 'utils/api/config';
import { getLanguage } from 'utils/language/language';
import { extractAttributeValuesAll } from 'utils/ecommerce/services/extract';

interface IJawazakContentWrapperProps {
	roamingEnabled: boolean;
	termsAndConditions: types.services.termsAndConditions;
	showOnlyActive: boolean;
	updateOrderItems: types.services.updateOrderItemsFunction;
	packages: Package[];
	setPendingStatus: (status: boolean) => void;
	roamingPending: boolean;
	subscriberProducts: SingleSubscriberProduct[];
	setIsJawazakActive: React.Dispatch<React.SetStateAction<boolean>>;
}

const JawazakContentWrapper = ({
	showOnlyActive,
	termsAndConditions,
	updateOrderItems,
	roamingEnabled,
	packages,
	setPendingStatus,
	roamingPending,
	subscriberProducts,
	setIsJawazakActive,
}: IJawazakContentWrapperProps) => {
	const [catalogEntryView, setCatalogEntryView] = useState<types.ecommerce.CatalogEntryView[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [roamingDialogOpen, setRoamingDialogOpen] = useState<boolean>(false);
	const [showDialog, setShowDialog] = useState<boolean>(false);
	const isMounted = useRef(true);

	const language = getLanguage();

	const showActivationDialog = () => {
		setShowDialog(true);
	};

	const hideActivationDialog = () => {
		setShowDialog(false);
	};

	const showRoamingDialog = () => {
		setRoamingDialogOpen(true);
	};

	const hideRoamingDialog = () => {
		setRoamingDialogOpen(false);
	};

	const updateJawazak = () => {
		if (roamingEnabled) {
			showActivationDialog();
		} else {
			showRoamingDialog();
		}
	};

	const activePackages: Package[] = useMemo(() => {
		return packages.filter((singlePackage) => {
			const productType = singlePackage.commerceAttributes?.productType;
			return productType === 'JAWAZAK';
		});
	}, [packages]);

	const pendingSubscriberProductIds = useMemo(
		() => subscriberProducts.filter(({ status }) => status !== 'Active').map(({ productId }) => productId),
		[subscriberProducts]
	);

	const pendingPackages: types.ecommerce.CatalogEntryView[] = useMemo(() => {
		return catalogEntryView.filter(({ partNumber }) => {
			return pendingSubscriberProductIds?.indexOf(getProductId(partNumber)) >= 0;
		});
	}, [catalogEntryView, pendingSubscriberProductIds]);

	const availablePackages: types.services.IPackage[] = useMemo(() => {
		const isPending: boolean = pendingPackages.length > 0;
		const activeStatus: types.services.IStatus = {
			type: 'ACTIVE',
		};
		const pendingStatus: types.services.IStatus = {
			type: 'PENDING',
		};
		const status: types.services.IStatus | undefined =
			isPending || activePackages.length > 0
				? undefined
				: {
						type: 'INACTIVE',
				  };
		return [
			{
				name: translate('number.services.mobile.jawazak.roaming'),
				description: translate('number.services.jawazak.description'),
				id: 'JAWAZAK',
				status,
				legacyProductId: '',
				place: '',
				buttons: [
					{
						text: translate('number.services.top.up'),
						color: 'primary-light',
						disabled: isPending,
						type: 'TOP_UP',
					},
				],
			},
			...activePackages.map((singleActivePackage) => {
				const description = jawazakPackageDescription(singleActivePackage);
				const name = jawazakPackageName(singleActivePackage);
				const activePendingPackage = subscriberProducts.find(({ productId, status }) => {
					return (
						status !== 'Active' &&
						(productId === getProductId(singleActivePackage.packageId) ||
							productId === getProductId(singleActivePackage.commerceUniqueIdToActivate))
					);
				});
				return {
					name,
					description,
					id: singleActivePackage.packageId,
					status: activePendingPackage
						? { ...pendingStatus, date: activePendingPackage.startDate }
						: activeStatus,
					legacyProductId: '',
					place: '',
					buttons: [],
				};
			}),
		];
	}, [activePackages, pendingPackages, subscriberProducts, language]);

	const filteredPackages = useMemo(() => {
		return showOnlyActive
			? availablePackages.filter(
					(singlePackage) =>
						singlePackage.status?.type === 'ACTIVE' ||
						(singlePackage.id === 'JAWAZAK' && !singlePackage.status)
			  )
			: availablePackages;
	}, [availablePackages, showOnlyActive]);

	useEffect(() => {
		setLoading(true);
		ECommerceService.getGroupContent(idConfig.SERVICES_TABLES.JAWAZAK[env])
			.pipe(finalize(() => isMounted.current && setLoading(false)))
			.subscribe(({ response }: any) => {
				if (isMounted.current) {
					const _catalogEntryView = response.catalogEntryView;
					if (_catalogEntryView && Array.isArray(_catalogEntryView) && _catalogEntryView.length > 0) {
						setCatalogEntryView(_catalogEntryView);
					}
				}
			});
	}, []);

	const validateReqProd = useCallback(
		(singleCatalogEntryView: types.ecommerce.CatalogEntryView) => {
			const subscriberProductIds = subscriberProducts.map(({ productId }) => productId);
			const { attributes } = singleCatalogEntryView;
			const reqProdIds = extractAttributeValuesAll({ attributes }, 'REQ_PROD').map((singleAttribute) =>
				getProductId(singleAttribute)
			);
			for (let i = 0; i < reqProdIds.length; i++) {
				if (subscriberProductIds.indexOf(reqProdIds[i]) !== -1) {
					return true;
				}
			}
			return false;
		},
		[subscriberProducts]
	);

	const activationAllowedPackages = catalogEntryView.filter((singleEntryView) => {
		return validateReqProd(singleEntryView);
	});

	useEffect(() => {
		setPendingStatus(pendingPackages.length > 0);
		// eslint-disable-next-line
	}, [pendingPackages]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		setIsJawazakActive(filteredPackages.some((pckg) => pckg.status?.type === 'ACTIVE'));
	}, [filteredPackages]);

	return (
		<>
			<ConditionalRender
				show={loading}
				onTrue={() => <CustomCircularProgress text={translate('number.loading.packages')} loading={loading} />}
				onFalse={() => (
					<AccordionContent
						elements={filteredPackages}
						updateService={updateJawazak}
						termsAndConditions={termsAndConditions}
					/>
				)}
			/>
			<JawazakActivationDialog
				catalogEntryView={activationAllowedPackages}
				isOpen={showDialog}
				hideDialog={hideActivationDialog}
				termsAndConditions={{
					url: apiConfig.links.roamingPlans,
					type: 'link',
					text: [
						translate('number.services.jawazak.one.plan'),
						translate('number.services.jawazak.subscribe.again'),
					],
				}}
				updateOrderItems={updateOrderItems}
			/>
			<RoamingInternationalActivationDialog
				isOpen={roamingDialogOpen}
				hideDialog={hideRoamingDialog}
				type="JAWAZAK"
				updateOrderItems={updateOrderItems}
				isPending={roamingPending}
			/>
		</>
	);
};

export default JawazakContentWrapper;
